import { Controller } from "@hotwired/stimulus";
import { Map, LngLat, Popup } from 'mapbox-gl';
import * as $ from 'jquery';

import { ajaxErrorHandler, formSubmitWithWaitForJobCompletion, postRequestAndWaitForJobCompletion } from '../../scripts/jobCompletion';
import { AddPointOfInterestToTourSearchResultModel } from '../../scripts/_generated/AddPointOfInterestToTourSearchResultModel';
import { reloadWithTurbo } from '../../scripts/turboHelper';
import TourMapController from "../TourMap/TourMapController";
import { getScoreFunctionPreservingOrderInSearch, selectize } from "../../scripts/selectizeHelper";
import { TourType } from "../../scripts/_generated/TourType";
import { DomEvents } from "../../scripts/_generated/DomEvents";
import { OnboardingElementIdentifiers } from "../../scripts/_generated/OnboardingElementIdentifiers";
import { StimulusControllers } from "../../scripts/_generated/StimulusControllers";

export default class AddDestinationPoiToTourController extends Controller {
    static targets = [
        "searchPoisInput"
    ];
    static values = {
        tourUniqueId: String,
        tourLanguage: String,
        getJobUrl: String,
        getDestinationPoisUrl: String,
        addDestinationPoiUrl: String,
        tourType: Number,
    }

    readonly searchPoisInputTarget!: HTMLInputElement;

    readonly tourUniqueIdValue!: string;
    readonly tourLanguageValue!: string;
    readonly getJobUrlValue!: string;
    readonly getDestinationPoisUrlValue!: string;
    readonly addDestinationPoiUrlValue!: string;
    readonly tourTypeValue!: TourType;

    connect() {
        let search: Selectize.IApi<string, AddPointOfInterestToTourSearchResultModel> | undefined = undefined;
        const getDestinationPoisUrl = this.getDestinationPoisUrlValue;
        const addDestinationPoiUrl = this.addDestinationPoiUrlValue;
        const getJobUrl = this.getJobUrlValue;

        selectize($(this.searchPoisInputTarget), {
            maxItems: 1,
            labelField: 'name',
            valueField: 'uniqueId',
            searchField: 'name',
            sortField: [
                {
                    field: 'priority',
                    direction: 'desc'
                }
            ],
            diacritics: true,
            preload: 'focus',
            load: (query, callback) => {
                var getDestinationPoisUrlWithSearchedText = getDestinationPoisUrl.replace("$searchedText", query);

                $.ajax({
                    url: getDestinationPoisUrlWithSearchedText,
                    type: "GET",
                    error: (jqXhr, textStatus, errorThrown) => {
                        ajaxErrorHandler(jqXhr, textStatus, errorThrown, getDestinationPoisUrl, undefined);
                    },
                    success: function (res) {
                        callback(res);
                    },
                });
            },
            onChange: (value) => {
                if (!search?.options) return;
                const option = search.options[value] as AddPointOfInterestToTourSearchResultModel;
                if (!option) return;

                if (this.tourTypeValue == TourType.Indoor) {

                    const formData = new FormData();
                    formData.append("poiContentUniqueId", `${option.uniqueId}`);

                    postRequestAndWaitForJobCompletion(addDestinationPoiUrl, formData, getJobUrl, undefined, () => {
                        reloadWithTurbo();
                    });

                } else {
                    const tourMapController = this.getTourMapController();
                    this.createPopupForDestinationPoi(option, tourMapController.map);
                }

                search.clear(true);
            },
            onDropdownClose: () => {
                search?.clearOptions();
            },
            score: getScoreFunctionPreservingOrderInSearch
        });
        search = this.searchPoisInputTarget.selectize;
    }

    popupForDestinationPoi: Popup | undefined;

    createPopupForDestinationPoi(poi: AddPointOfInterestToTourSearchResultModel, map: Map) {
        if (this.popupForDestinationPoi) {
            this.popupForDestinationPoi.remove();
        }

        const addPoiUrl = this.addDestinationPoiUrlValue
            .replace('$language', this.tourLanguageValue)
            .replace('$tourUniqueId', this.tourUniqueIdValue);

        const html =
            `<div class="d-flex flex-column m-2">
                <div class="font-weight-bold" style="max-width: 200px">
                        ${poi.name}
                </div>
                <div class="mt-2">
                    <form action="${addPoiUrl}" method="post" id="createPopupForDestinationPoiForm">
                        <input name="poiContentUniqueId" type="hidden" value="${poi.uniqueId}"/>
                        <button name="submit" 
                                type="submit" 
                                class="form-control btn btn-sm btn-outline-primary ${OnboardingElementIdentifiers.tourDetailPoiToAddToTourFormCssClass}">
                            <i class="fas fa-plus-circle"></i> Add to tour
                        </button>
                    </form>
                </div>
            </div>`;

        this.popupForDestinationPoi = new Popup({ closeOnClick: true, closeOnMove: true, focusAfterOpen: true })
            .setLngLat(new LngLat(poi.coordinates.longitude, poi.coordinates.latitude))
            .setHTML(html)
            .addTo(map);

        formSubmitWithWaitForJobCompletion(
            $('#createPopupForDestinationPoiForm'),
            $('#createPopupForDestinationPoiForm button'),
            this.getJobUrlValue,
            () => {
                reloadWithTurbo();
            }
        );
        
        this.mapMoveEndEventListener = () => this.raisePoiAddedToMapToAddToTheTourDomEvent(map);

        map.on("moveend", this.mapMoveEndEventListener);

        map.flyTo({ center: new LngLat(poi.coordinates.longitude, poi.coordinates.latitude) });
    }

    mapMoveEndEventListener: (() => void) | undefined;

    raisePoiAddedToMapToAddToTheTourDomEvent(map: Map) {
        if (!this.mapMoveEndEventListener) throw new Error("mapMoveEndEventListener is undefined");
        map.off("moveend", this.mapMoveEndEventListener);

        this.dispatch(DomEvents.tourDetailPoiAddedToMapToAddToTheTour);
    }


    private getTourMapController(): TourMapController {
        const tourMapElement = $(`[data-controller='${StimulusControllers.tourMap}']`);
        if (tourMapElement.length === 0) throw new Error(`Cannot find ${StimulusControllers.tourMap} controller element.`)
        return this.application.getControllerForElementAndIdentifier(tourMapElement[0], StimulusControllers.tourMap) as TourMapController;
    }    
}
